import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import getServices from '../../services/serviceRouter';

/**
 * useDictationRecorder — MediaRecorder-based audio capture
 * with chunked upload pipeline, retry/backoff, and finalize.
 *
 * When appMode === 'demo', mic access is blocked and dictation_enabled=false.
 * The existing timer simulation fallback runs instead (no code change needed in caller).
 *
 * States: idle → recording → paused → recording → stopped → finalizing → done
 */

const CHUNK_INTERVAL_MS = 15_000; // 15s chunks
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export default function useDictationRecorder({ onTranscriptReady, onError, appMode }) {
  const isDemo = appMode === 'demo';
  // Memoize the resolved service router so its identity is stable across
  // renders for a given appMode — keeps every downstream hook dep array
  // (effects + callbacks below) referentially correct without forcing
  // them to re-run on every parent render.
  const dictSvc = useMemo(() => getServices(appMode).dictation, [appMode]);
  const [dictationEnabled, setDictationEnabled] = useState(null); // null = loading
  const [diarizationEnabled, setDiarizationEnabled] = useState(false);
  const [diarize, setDiarize] = useState(false); // per-session user preference
  const [sessionId, setSessionId] = useState(null);
  const [recState, setRecState] = useState('idle'); // idle|recording|paused|stopped|finalizing|done|error
  const [chunkQueue, setChunkQueue] = useState([]);
  const [chunksUploaded, setChunksUploaded] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [finalTranscript, setFinalTranscript] = useState(null);
  const [segments, setSegments] = useState([]);
  const [speakers, setSpeakers] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const timerRef = useRef(null);
  const lastChunkTimeRef = useRef(0);
  const uploadingRef = useRef(false);

  // Check feature flag on mount (blocked in demo)
  useEffect(() => {
    if (isDemo) { setDictationEnabled(false); setDiarizationEnabled(false); return; }
    dictSvc.getStatus()
      .then((data) => {
        setDictationEnabled(data.dictation_enabled === true);
        setDiarizationEnabled(data.diarization_enabled === true);
        // Ambient mode defaults diarize ON server-side; mirror that here
        // so the UI toggle reflects reality before the user touches it.
        if (data.diarization_enabled === true) setDiarize(true);
      })
      .catch(() => { setDictationEnabled(false); setDiarizationEnabled(false); });
  }, [isDemo, dictSvc]);

  // Timer
  useEffect(() => {
    if (recState === 'recording') {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [recState]);

  // Process upload queue
  useEffect(() => {
    if (uploadingRef.current || chunkQueue.length === 0 || !sessionId) return;

    const processQueue = async () => {
      uploadingRef.current = true;
      const queue = [...chunkQueue];
      const remaining = [];

      for (const item of queue) {
        let uploaded = false;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          try {
            await dictSvc.uploadChunk(
              sessionId,
              item.blob,
              item.index,
              item.durationMs,
            );
            setChunksUploaded((c) => c + 1);
            uploaded = true;
            break;
          } catch (err) {
            if (attempt < MAX_RETRIES - 1) {
              await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
            }
          }
        }
        if (!uploaded) {
          remaining.push({ ...item, retries: (item.retries || 0) + MAX_RETRIES });
        }
      }

      setChunkQueue(remaining);
      uploadingRef.current = false;
    };

    processQueue();
  }, [chunkQueue, sessionId, dictSvc]);

  const start = useCallback(async (patientId, patientName, mode, chiefComplaint) => {
    try {
      // 1. Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // 2. Create backend session
      const data = await dictSvc.createSession({
        patient_id: patientId,
        patient_name: patientName,
        mode: mode || 'dictation',
        chief_complaint: chiefComplaint || '',
        diarize: !!diarize,
      });
      setSessionId(data.session_id);
      // Mirror server-resolved diarize (takes feature flag + mode into account).
      if (typeof data.diarize === 'boolean') setDiarize(data.diarize);

      // 3. Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunkIndexRef.current = 0;
      lastChunkTimeRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          const now = Date.now();
          const durationMs = now - lastChunkTimeRef.current;
          lastChunkTimeRef.current = now;
          const idx = chunkIndexRef.current++;

          setTotalChunks((t) => t + 1);
          setChunkQueue((q) => [
            ...q,
            { blob: e.data, index: idx, durationMs, retries: 0 },
          ]);
        }
      };

      recorder.start(CHUNK_INTERVAL_MS);
      setRecState('recording');
      setElapsed(0);
      setChunksUploaded(0);
      setTotalChunks(0);
      setChunkQueue([]);
      setFinalTranscript(null);
      setSegments([]);
      setSpeakers(null);
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone permission.'
        : err.message || 'Failed to start recording';
      onError?.(msg);
      setRecState('error');
    }
  }, [onError, diarize, dictSvc]);

  const pause = useCallback(async () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
    }
    if (sessionId) {
      try { await dictSvc.updateSession(sessionId, 'pause'); } catch {}
    }
    setRecState('paused');
  }, [sessionId, dictSvc]);

  const resume = useCallback(async () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
    }
    if (sessionId) {
      try { await dictSvc.updateSession(sessionId, 'resume'); } catch {}
    }
    setRecState('recording');
  }, [sessionId, dictSvc]);

  const stop = useCallback(async () => {
    // Stop MediaRecorder (fires final ondataavailable)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    // Stop mic stream
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (sessionId) {
      try { await dictSvc.updateSession(sessionId, 'stop'); } catch {}
    }
    setRecState('stopped');
  }, [sessionId, dictSvc]);

  const finalize = useCallback(async () => {
    if (!sessionId) return;
    setRecState('finalizing');

    // Wait for remaining chunk uploads (up to 30s)
    const deadline = Date.now() + 30_000;
    while (chunkQueue.length > 0 || uploadingRef.current) {
      if (Date.now() > deadline) break;
      await new Promise((r) => setTimeout(r, 500));
    }

    try {
      const data = await dictSvc.finalizeSession(sessionId);
      setFinalTranscript(data.transcript);
      setSegments(Array.isArray(data.segments) ? data.segments : []);
      setSpeakers(data.speakers || null);
      setRecState('done');
      onTranscriptReady?.(data.transcript, data.method, sessionId, {
        segments: data.segments || [],
        speakers: data.speakers || null,
      });
    } catch (err) {
      onError?.('Finalization failed: ' + (err.message || 'Unknown error'));
      setRecState('error');
    }
  }, [sessionId, chunkQueue, onTranscriptReady, onError, dictSvc]);

  const cancel = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    clearInterval(timerRef.current);
    setRecState('idle');
    setSessionId(null);
    setChunkQueue([]);
    setElapsed(0);
    setSegments([]);
    setSpeakers(null);
  }, []);

  return {
    dictationEnabled,
    diarizationEnabled,
    diarize,
    setDiarize,
    sessionId,
    recState,
    elapsed,
    chunksUploaded,
    totalChunks,
    chunkQueue,
    finalTranscript,
    segments,
    speakers,
    start,
    pause,
    resume,
    stop,
    finalize,
    cancel,
  };
}
